import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Inbox, 
  Send, 
  ArrowLeftRight, 
  Package, 
  FileText, 
  BarChart3,
  Users,
  Warehouse,
  Box,
  ClipboardCheck,
  MapPin,
  Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { filterOperations } from '@/utils/operationAccess';
import { moduleReportTiles } from '@/lib/sectionModules';
import { JoStuckBanner, STAGE_SETS } from '@/components/processingJo/JoStuckBanner';

const DispatchDashboard = () => {
  const navigate = useNavigate();
  
  const basePath = '/sections/dispatch';
  
  // Dispatch section specific tiles with priority WMS tiles at top
  const tiles = [
    // Priority WMS tiles
    {
      id: 'traffic-control',
      title: 'Traffic Control',
      description: 'Monitor & manage warehouse row reservations',
      icon: MapPin,
      iconBg: 'bg-orange-500',
      route: `${basePath}/traffic-control`,
      testId: 'traffic-control-tile'
    },
    {
      id: 'dispatch-supervisor',
      title: 'Dispatch Supervisor',
      description: 'Manage pickers & auto-allocate pick slips',
      icon: Users,
      iconBg: 'bg-indigo-500',
      route: '/wms/dispatch-supervisor',
      testId: 'dispatch-supervisor-tile'
    },
    {
      id: 'sfg-wms',
      title: 'SFG WMS',
      description: 'Semi-Finished Goods Warehouse Management',
      icon: Warehouse,
      iconBg: 'bg-purple-500',
      route: '/wms/sfg',
      testId: 'sfg-wms-tile'
    },
    {
      id: 'fg-wms',
      title: 'FG WMS',
      description: 'Finished Goods Warehouse Management',
      icon: Box,
      iconBg: 'bg-emerald-500',
      route: '/wms/fg',
      testId: 'fg-wms-tile'
    },
    // Standard section tiles
    {
      id: 'pieces-processed',
      title: 'Pieces Processed',
      description: 'Track staff work for wage calculation',
      icon: ClipboardCheck,
      iconBg: 'bg-green-500',
      route: `${basePath}/pieces-processed`,
      testId: 'pieces-processed-tile'
    },
    {
      id: 'inward-consignment',
      title: 'Inward Consignment',
      description: 'Receive and process incoming consignments',
      icon: Inbox,
      iconBg: 'bg-teal-500',
      route: `${basePath}/inward-consignment`,
      testId: 'inward-consignment-tile'
    },
    {
      id: 'shipment-request',
      title: 'Shipment Request',
      description: 'View and process incoming shipment requests',
      icon: Send,
      iconBg: 'bg-orange-500',
      route: `${basePath}/shipment-request`,
      testId: 'shipment-request-tile'
    },
    {
      id: 'internal-transfer',
      title: 'Internal Transfers',
      description: 'Handle transfers within/between sections',
      icon: ArrowLeftRight,
      iconBg: 'bg-blue-500',
      route: `${basePath}/internal-transfer`,
      testId: 'internal-transfer-tile'
    },
    {
      id: 'bundle-receipt',
      title: 'Bundle Receipt Entry',
      description: 'Process received bundles',
      icon: Package,
      iconBg: 'bg-cyan-500',
      route: `${basePath}/bundle-receipt`,
      testId: 'bundle-receipt-tile'
    },
    {
      id: 'bundle-receipt-report',
      title: 'Bundle Receipt Report',
      description: 'View all received bundles with details',
      icon: FileText,
      iconBg: 'bg-amber-500',
      route: `${basePath}/bundle-receipt-report`,
      testId: 'bundle-receipt-report-tile'
    },
    {
      id: 'day-wise-report',
      title: 'Day Wise Transaction Report',
      description: 'Daily summary report',
      icon: FileText,
      iconBg: 'bg-slate-600',
      route: `${basePath}/day-wise-report`,
      testId: 'day-wise-report-tile'
    },
    {
      id: 'closing-stock',
      title: 'Closing Stock Report',
      description: 'Live closing stock by operation',
      icon: BarChart3,
      iconBg: 'bg-teal-600',
      route: `${basePath}/closing-stock`,
      testId: 'closing-stock-tile'
    },
    {
      id: 'internal-transfer-report',
      title: 'Internal Transfer Report',
      description: 'View all received internal transfers',
      icon: FileText,
      iconBg: 'bg-indigo-600',
      route: `${basePath}/internal-transfer-report`,
      testId: 'internal-transfer-report-tile'
    },
    {
      id: 'footprint-movement-report',
      title: 'Footprint Movement Report',
      description: 'Track put away & picking in FG/SFG storage',
      icon: BarChart3,
      iconBg: 'bg-cyan-600',
      route: `${basePath}/footprint-movement-report`,
      testId: 'footprint-movement-report-tile'
    },
    {
      id: 'usn-tracking',
      title: 'USN Tracking',
      description: 'Track complete USN movement history',
      icon: Package,
      iconBg: 'bg-violet-600',
      route: `${basePath}/usn-tracking`,
      testId: 'usn-tracking-tile'
    },
    {
      id: 'transport-master',
      title: 'Transport Master',
      description: 'Manage transporters, freight rates & auto-selection',
      icon: Truck,
      iconBg: 'bg-blue-600',
      route: `${basePath}/transport-master`,
      testId: 'transport-master-tile'
    }
  ];

  const reportTiles = moduleReportTiles('dispatch', { itId: 'internal-transfer-out-report' })
    .map((t) => (t.id === 'internal-transfer-out-report' ? { ...t, title: 'Outgoing Transfer Report' } : t));
  const filteredTiles = filterOperations('dispatch', [...tiles, ...reportTiles]);
  const wmsTiles = filteredTiles.filter(t => ['traffic-control', 'dispatch-supervisor', 'sfg-wms', 'fg-wms'].includes(t.id));
  const sectionTiles = filteredTiles.filter(t => !['traffic-control', 'dispatch-supervisor', 'sfg-wms', 'fg-wms'].includes(t.id));

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Dispatch Section</h1>
              <p className="text-sm text-[#E8DCC8]">Final Dispatch & Warehouse Management</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <JoStuckBanner stages={STAGE_SETS.sfg} testId="dispatch-jo-stuck-banner" className="mb-6" />
        {/* WMS Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">Warehouse Management</h2>
          <p className="text-gray-600 mt-1">SFG & FG Put-Away, Picking & Allocation</p>
        </div>

        {/* WMS Tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {wmsTiles.map((tile) => {
            const IconComponent = tile.icon;
            return (
              <Card 
                key={tile.id}
                className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md"
                onClick={() => navigate(tile.route)}
                data-testid={tile.testId}
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className={`w-16 h-16 ${tile.iconBg} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {tile.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Section Operations */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">Section Operations</h2>
          <p className="text-gray-600 mt-1">Consignments, Transfers & Reports</p>
        </div>

        {/* Section Tiles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {sectionTiles.map((tile) => {
            const IconComponent = tile.icon;
            return (
              <Card 
                key={tile.id}
                className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md"
                onClick={() => navigate(tile.route)}
                data-testid={tile.testId}
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className={`w-16 h-16 ${tile.iconBg} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {tile.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default DispatchDashboard;
